import {
  CSSProperties,
  useState,
  forwardRef,
  createRef,
  useImperativeHandle,
  useCallback,
  useEffect,
} from "react";
import PaymentForm, {
  ServiceCollection,
  TokenizerService,
  ApplicationService,
  PaymentFormRef,
  PaymentFormLoadingContentContainer,
  PaymentFormLoadingFailedContentContainer,
} from "@clarkinc/payment-client";
import { cls } from "cls";
import { Icon } from "@clarkinc/zest-design-system/ui";
import { PaymentMethodDetails } from "../../../models";
import { PaymentSelectionRef, tokenize } from "../../paymentSelectionRefs";
import { env } from "~/common/Environment";
import SavedCardDropdown from "./SavedCardDropdown";
import spritePath from "~/common/Icons/Zest/remixicon.symbol.v1.svg";

interface Props {
  defaultTokenId?: number;
  defaultSavedCards: PaymentMethodDetails[];
  setSavedCardId: (val: number | undefined) => void;
}

function getTokenizerDomain() {
  if (env.isLoc || env.isDev) return "dev.webstaurantstore.com";
  if (env.isTest) return "test.webstaurantstore.com";
  return "webstaurantstore.com";
}

const PaymentSelection = forwardRef<PaymentSelectionRef, Props>(
  ({ defaultTokenId, defaultSavedCards, setSavedCardId }, ref) => {
    const hasSavedCards = defaultSavedCards.length > 0;
    const [usingSavedCards, setUsingSavedCards] = useState(hasSavedCards);
    const [tokenId, setTokenId] = useState(defaultTokenId);
    const [savedCards, setSavedCards] =
      useState<PaymentMethodDetails[]>(defaultSavedCards);
    const [renderIframe, setRenderIframe] = useState(!hasSavedCards);

    const toggleForm = () => {
      setUsingSavedCards(!usingSavedCards);
      setRenderIframe(usingSavedCards);
      return false;
    };

    const serviceCollection = new ServiceCollection(
      new TokenizerService(`https://tokenizer.${getTokenizerDomain()}`),
      new ApplicationService(
        `${window.location.origin}/myaccount:usercard/createrenderformtoken/`,
        `${window.location.origin}/myaccount:usercard/createtokenizecardtoken/`,
        `${window.location.origin}/myaccount:usercard/validatetokenizecardresulttoken/`,
      ),
    );

    const toggleLabel = usingSavedCards ? "Add a new card" : "Use a saved card";
    const paymentFormRef = createRef<PaymentFormRef>();

    const updateSelectedCard = useCallback(
      (newCard: PaymentMethodDetails) => {
        const existingCard = savedCards.find((x) => x.id === newCard.id);

        if (existingCard !== undefined) {
          existingCard.name = newCard.name;
          setSavedCards([...savedCards]);
        } else {
          setSavedCards([...savedCards, newCard]);
        }

        setTokenId(newCard.id);
        setSavedCardId(newCard.id);
      },
      [savedCards, setSavedCardId],
    );

    const useableTokenId = usingSavedCards ? tokenId : undefined;

    useImperativeHandle(
      ref,
      () => ({
        tokenize: tokenize(paymentFormRef, updateSelectedCard, useableTokenId),
        showIframe: () => setRenderIframe(true),
        hideIframe: () => setRenderIframe(false),
      }),
      [paymentFormRef, updateSelectedCard, useableTokenId],
    );

    const savedCardStyles: CSSProperties = {};
    const newCardAlertStyles: CSSProperties = {};
    const newCardIframeStyles: CSSProperties = {
      border: "none",
      width: "100%",
      height: "100px",
    };

    if (!hasSavedCards) {
      savedCardStyles.display = "none";
    }

    useEffect(() => {
      if (savedCards.length === 0 && defaultSavedCards.length > 0) {
        setSavedCards(defaultSavedCards);
        setUsingSavedCards(true);
        setRenderIframe(false);
      }
    }, [savedCards, defaultSavedCards]);

    return (
      <>
        <p className="pb-2 m-0 text-xs font-normal text-gray-800">
          Your payment method for your recurring monthly subscription.
        </p>
        {hasSavedCards && usingSavedCards && savedCards.length ? (
          <SavedCardDropdown
            isDisabled={false}
            tokenId={tokenId}
            savedCards={savedCards}
            updateSelectedCard={updateSelectedCard}
            styles={savedCardStyles}
          />
        ) : null}
        {renderIframe && (
          <div data-testid="plus-modal-payment-form">
            <PaymentForm
              setNewCardAsDefault={false}
              serviceCollection={serviceCollection}
              containerStyle={newCardIframeStyles}
              ref={paymentFormRef}
            >
              <PaymentFormLoadingContentContainer>
                <div style={newCardAlertStyles} className="pb-0">
                  <div className="flex flex-wrap py-1 animate-pulse">
                    <div className="h-10 mb-2 bg-gray-200 rounded w-full" />
                    <div className="h-10 mb-2 bg-gray-200 rounded w-full" />
                    <div className="flex w-full -mr-2">
                      <div className="h-10 mb-2 bg-gray-200 rounded w-1/3" />
                      <div className="h-10 mb-2 ml-2 bg-gray-200 rounded w-1/3" />
                      <div className="h-10 mb-2 ml-2 bg-gray-200 rounded w-1/3" />
                    </div>
                  </div>
                </div>
              </PaymentFormLoadingContentContainer>
              <PaymentFormLoadingFailedContentContainer>
                <div style={newCardAlertStyles}>
                  <p className="p-2 text-sm text-center bg-red-200 border border-red-300 border-solid rounded text-red-900">
                    Unable to load credit card form
                  </p>
                </div>
              </PaymentFormLoadingFailedContentContainer>
            </PaymentForm>
          </div>
        )}
        {hasSavedCards && savedCards.length ? (
          <button
            type="button"
            className={cls(
              "cursor-pointer",
              "text-sm",
              "border-none",
              "bg-transparent",
              "text-green-500",
              "pb-5",
              "pl-0",
              "font-semibold",
              usingSavedCards && "pt-4",
            )}
            onClick={toggleForm}
            data-testid="plus-modal-payment-form-toggle"
          >
            <Icon
              name="ri-add-line"
              size="xsmall"
              spriteSheetHref={spritePath}
              className="fill-green-500 align-middle"
            />
            <span className="align-middle">{toggleLabel}</span>
          </button>
        ) : null}
      </>
    );
  },
);

export default PaymentSelection;
